import { useRef, useState } from "react";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import LocationPicker from "components/generic/LocationPicker";
import CropProfileModal from "./CropProfileModal";
import { compressImage } from "lib/utils";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

import {
  ArrowLeft,
  Eye,
  EyeOff,
  Camera,
  AlertTriangle,
  CheckCircle2,
  X,
  Loader2,
} from "lucide-react";

import toast from "react-hot-toast";
import { distanceOptions } from "data/staticData";
import { Link } from "react-router-dom";
import { signUpStep1 } from "services/authService";

export default function Step1Form({ data, setData, nextStep, prevStep }) {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  /* =========================
     TOAST HELPERS
  ========================== */

  const showErrorToast = (title, message) => {
    toast.custom(
      (t) => (
        <div
          className={`${t.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
        >
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  const showSuccessToast = (title, message) => {
    toast.custom(
      (t) => (
        <div
          className={`${t.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-[#F0FDF4] border border-[#22C55E] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
        >
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  /* =========================
     IMAGE HANDLING
  ========================== */
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressing(true); // Start Loader
      const compressedFile = await compressImage(file);

      setSelectedImage({
        file: compressedFile,
        preview: URL.createObjectURL(compressedFile),
      });

      setCropModalOpen(true);
    } catch (error) {
      console.error("Processing failed:", error);
    } finally {
      setIsCompressing(false); // Stop Loader
    }
  };

  const handleCropSave = (croppedBlob, croppedPreview) => {
    const croppedFile = new File([croppedBlob], "profile.png", {
      type: "image/png",
    });

    setData({
      ...data,
      profileImage: croppedFile,
      profilePreview: croppedPreview,
    });

    setCropModalOpen(false);
    setSelectedImage(null);
  };

  /* =========================
     SUBMIT (ALL FIELDS REQUIRED)
  ========================== */

  const handleSubmit = async () => {
    // 1. Name Validation
    if (!data.firstName?.trim()) {
      showErrorToast("Validation Error", "First name is required.");
      return;
    }
    if (!data.lastName?.trim()) {
      showErrorToast("Validation Error", "Last name is required.");
      return;
    }

    // 2. Email Validation
    if (!data.email?.trim()) {
      showErrorToast("Validation Error", "Email is required.");
      return;
    }
    if (!isValidEmail(data.email)) {
      showErrorToast("Invalid Email", "Please enter a valid email address.");
      return;
    }

    if (!data.phone_number?.trim()) {
      showErrorToast("Validation Error", "Phone number is required.");
      return;
    }
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(data.phone_number)) {
      showErrorToast("Invalid Phone Number", "Please enter a valid phone number.");
      return;
    }

    // 3. Password Validation
    if (!data.password?.trim()) {
      showErrorToast("Validation Error", "Password is required.");
      return;
    }
    if (data.password.length < 6) {
      showErrorToast("Weak Password", "Password must be at least 6 characters long.");
      return;
    }

    // 4. Location Validation
    if (!data.location) {
      showErrorToast("Validation Error", "Please select your location.");
      return;
    }

    // 5. Working Distance Validation
    if (!data.workingDistance) {
      showErrorToast("Validation Error", "Please select your working distance.");
      return;
    }

    // 6. Profile Image Validation
    if (!data.profileImage) {
      showErrorToast("Validation Error", "Please upload a profile picture.");
      return;
    }
    //   }
    //   if (!data.email?.trim()) {
    //     showErrorToast("Validation Error", "Email is required.");
    //     return;
    //   }
    //   if (!isValidEmail(data.email)) {
    //     showErrorToast("Invalid Email", "Please enter a valid email address.");
    //     return;
    //   }
    //   if (!data.password?.trim()) {
    //     showErrorToast("Validation Error", "Password is required.");
    //     return;
    //   }
    //   if (data.password.length < 6) {
    //     showErrorToast("Weak Password", "Password must be at least 6 characters long.");
    //     return;
    //   }

    //   try {
    //     setLoading(true);

    //     const formData = new FormData();
    //     formData.append("first_name", data.firstName);
    //     formData.append("last_name", data.lastName);
    //     formData.append("email", data.email);
    //     formData.append("password", data.password);
    //     formData.append("working_distance", data.workingDistance);

    //     if (data.crew_member_id) {
    //       formData.append("crew_member_id", data.crew_member_id);
    //     }
    //     if (data.user_id) {
    //       formData.append("user_id", data.user_id);
    //     }

    //     if (data.location) {
    //       formData.append("location", JSON.stringify(data.location));
    //     }

    //     if (data.profileImage) {
    //       formData.append("profile_photo", data.profileImage);
    //     }

    //     const res = await signUpStep1(formData);

    //     setData((prev) => ({
    //       ...prev,
    //       crew_member_id: res.crew_member_id,
    //       user_id: res.user_id
    //     }));

    //     showSuccessToast("Saved", "Step 1 completed successfully.");
    //     nextStep();
    //   } catch (error) {
    //     console.error("Step 1 error:", error);
    //     const apiMessage =
    //       error?.response?.data?.message ||
    //       error?.message ||
    //       "Step 1 failed. Please try again.";
    //     showErrorToast("Signup Failed", apiMessage);
    //   } finally {
    //     setLoading(false);
    //   }
    // };

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("first_name", data.firstName);
      formData.append("last_name", data.lastName);
      formData.append("email", data.email);
      formData.append("password", data.password);
      formData.append("working_distance", data.workingDistance);
      formData.append("phone_number", data.phone_number);

      if (data.crew_member_id) {
        formData.append("crew_member_id", data.crew_member_id);
      }
      if (data.user_id) {
        formData.append("user_id", data.user_id);
      }

      if (data.location) {
        formData.append("location", JSON.stringify(data.location));
      }

      if (data.profileImage) {
        formData.append("profile_photo", data.profileImage);
      }

      const res = await signUpStep1(formData);

      setData((prev) => ({
        ...prev,
        crew_member_id: res.crew_member_id,
        user_id: res.user_id
      }));

      showSuccessToast("Saved", "Step 1 completed successfully.");
      nextStep();
    } catch (error) {
      console.error("Step 1 error:", error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Step 1 failed. Please try again.";
      showErrorToast("Signup Failed", apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-10">
      <form className="space-y-4 pl-1" onSubmit={(e) => e.preventDefault()}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              First Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              placeholder="John"
              value={data.firstName || ""}
              onChange={(e) => setData({ ...data, firstName: e.target.value })}
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Last Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              placeholder="Doe"
              value={data.lastName || ""}
              onChange={(e) => setData({ ...data, lastName: e.target.value })}
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Email <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={data.email || ""}
              onChange={(e) => setData({ ...data, email: e.target.value })}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <Input
              type="tel"
              placeholder="Enter your phone number"
              value={data.phone_number || ""}
              onChange={(e) => setData({ ...data, phone_number: e.target.value })}
            />
          </div>

          {/* Password */}
          <div className="lg:col-span-2">
            <label className="text-sm font-medium mb-1.5 block">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={data.password || ""}
                onChange={(e) => setData({ ...data, password: e.target.value })}
                className="w-full pr-10"
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
        {/* Location */}
        <div className="w-full flex flex-col gap-2">
          <label className="text-sm font-medium mb-1.5 block">
            Location <span className="text-red-500">*</span>
          </label>
          <LocationPicker
            value={data.location}
            onChange={(v) => setData({ ...data, location: v })}
            placeholder="Add Your Location"
          />
        </div>

        {/* Working Distance */}
        <div className="flex flex-col items-start gap-2 w-full">
          <label className="text-sm font-medium mb-1.5 block">
            Shoot Radius <span className="text-red-500">*</span>
          </label>

          <Select
            value={data.workingDistance}
            onValueChange={(v) => setData({ ...data, workingDistance: v })}
          >
            <SelectTrigger className="w-full bg-background text-sm">
              <SelectValue placeholder="Select working distance" />
            </SelectTrigger>

            <SelectContent className="bg-popover text-popover-foreground">
              {distanceOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Profile Picture */}
        <div className="max-w-xl rounded-2xl border border-[#F1E7D8] bg-white px-6 py-5">
          <h2 className="text-[16px] font-semibold text-[#1A1A1A]">
            Profile Picture <span className="text-red-500">*</span>
          </h2>

          <p className="mt-1 text-[13px] text-[#8F8F8F]">
            Add photo to build connection and trust
          </p>

          <div className="mt-4 flex items-center gap-4">
            <div className="h-14 w-14 rounded-full overflow-hidden bg-[#F3EFE8] flex items-center justify-center">
              {
                isCompressing ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="animate-spin h-6 w-6 text-[#E8D1AB]" />
                  </div>
                ) : (
                  <img
                    src={data.profilePreview || "/images/Group.png"}
                    alt="Profile"
                    className="h-full w-full object-contain p-1"
                  />
                )}
            </div>

            <button
              type="button"
              onClick={() => fileInputRef.current.click()}
              className="flex items-center gap-2 h-10 px-4 rounded-full border border-[#EADFCB] bg-[#FBF7F1] text-[14px] font-medium text-[#1A1A1A] transition hover:bg-[#F3E8D9]"
            >
              <Camera className="h-4 w-4 text-[#B9A47C]" />
              Upload Profile Picture
            </button>

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </form>

      <div className="flex justify-start gap-4">
        <button
          type="button"
          onClick={prevStep}
          className="flex w-10 h-10  items-center justify-center rounded-full border border-black/10 hover:bg-black/5"
        >
          <ArrowLeft className="w-5 h-5 text-black" />
        </button>

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="rounded-full border border-black/10 px-4 py-2"
        >
          {loading ? "Saving..." : "Next"}
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-10">
        <hr className="flex-grow" />
        <span>Already have an account?</span>
        <Link to="/loginCrew" className="text-black">
          Log in
        </Link>
        <hr className="flex-grow" />
      </div>

      {cropModalOpen && selectedImage && (
        <CropProfileModal
          image={selectedImage.preview}
          onClose={() => setCropModalOpen(false)}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
}
